import {
  getQueryStringParams,
  sortFunc as sortFuncUtil,
} from "../../shared/utils";

// Fields that have built-in reverse handling in their sort functions
const fieldsWithReversibleSortFunctions = [
  "score",
  "calculatedComparison",
  "dxiComparison",
  "priority",
  "avgValue",
  "annualizedCost",
];

/**
 * Reusable Alpine.js mixin for team tree expansion functionality
 *
 * Provides expandable/collapsible team hierarchy in breakdown tables.
 * Can be used across workflow, sentiment, DXI, and other breakdown components.
 *
 * Usage:
 * Alpine.data('myBreakdown', () => ({
 *   ...teamTreeExpansionMixin(),
 *
 *   // Implement these methods in your component:
 *   getChildDataEndpoint(parentRow) { return '/path/to/endpoint'; },
 *   getChildDataParams(parentRow) { return { param: value }; },
 *   getCachedSquads() { return this.squadsData || []; }
 * }));
 */
export const teamTreeExpansionMixin = () => ({
  // Tree expansion state
  expandedRows: new Set(),
  expandedRowsLoading: {},
  childRowsByParent: {},

  // Configuration for ID resolution (override in component as needed)
  // rowIdKey: which field on a data row uniquely identifies the team (default: 'snapshot_squad_id')
  // cachedIdKey: which field on a cached squad uniquely identifies it (default: 'id')
  // cachedParentKey: which field on a cached squad references its parent's id (default: 'parent_id')
  rowIdKey: "snapshot_squad_id",
  cachedIdKey: "id",
  cachedParentKey: "parent_id",

  /**
   * Check if any team data is currently being fetched
   */
  get isFetchingTeamData() {
    return Object.values(this.expandedRowsLoading).some((loading) => loading);
  },

  getRowId(row) {
    // Prefer explicit key, fall back to common keys; prefer snapshot_squad_id for server lookups
    return row?.[this.rowIdKey] ?? row?.snapshot_squad_id ?? row?.squad_id;
  },

  /**
   * Determine if a team row can be expanded (has children)
   * Only works in "team" aggregation mode
   */
  isTeamExpandable(row) {
    // Only expandable when using team tree aggregation (not leaf_teams or selected_teams)
    const aggregation =
      this.$store?.snapshotResultFilters?.aggregation ||
      getQueryStringParams().get("aggregation") ||
      "team";

    if (aggregation !== "team") {
      return false;
    }

    // Use the is_parent flag from the row data (included in SQL query)
    // This eliminates dependency on cached squad data
    return row.is_parent || false;
  },

  /**
   * Validate that required methods are implemented by the component
   * Throws descriptive errors if methods are missing
   */
  validateRequiredMethods() {
    const requiredMethods = [
      "getChildDataEndpoint",
      "getChildDataParams",
      "getCachedSquads",
    ];

    requiredMethods.forEach((method) => {
      if (
        typeof this[method] !== "function" ||
        this[method].toString().includes("throw new Error")
      ) {
        throw new Error(
          `teamTreeExpansionMixin requires ${method}() method to be implemented. ` +
            `This method is essential for team tree expansion functionality. ` +
            `Please implement this method in your component.`,
        );
      }
    });
  },

  /**
   * Handle expanding/collapsing a team row
   * Fetches child team data if not already loaded
   */
  async handleRowExpand(row) {
    // Validate required methods on first use
    this.validateRequiredMethods();

    const rowId = this.getRowId(row);
    const isBeingOpened = !this.expandedRows.has(rowId);

    // Toggle expanded state
    if (isBeingOpened) {
      this.expandedRows.add(rowId);
    } else {
      this.expandedRows.delete(rowId);
    }

    // If row is now expanded and we don't have data for its children yet
    if (isBeingOpened && !this.childRowsByParent[rowId]) {
      this.expandedRowsLoading[rowId] = true;

      try {
        // Get endpoint and params from implementing component
        const endpoint = this.getChildDataEndpoint(row);
        const baseParams = this.getChildDataParams(row) || {};

        // Let server find children based on parent ID
        const childParams = {
          ...baseParams,
          aggregation: "selected_teams", // Always use selected_teams for children
          parent_squad_id: rowId, // Use snapshot_squad_id for parent lookup
        };

        // Remove empty/null-like params so we don't send invalid values like prev_ss=null
        Object.keys(childParams).forEach((key) => {
          const value = childParams[key];
          if (
            value == null ||
            value === "null" ||
            value === "undefined" ||
            value === ""
          ) {
            delete childParams[key];
          }
        });

        const queryString = new URLSearchParams(childParams).toString();
        const response = await fetch(`${endpoint}?${queryString}`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();

        // Store child data for this parent
        const childRows = data.result_items || data || [];

        // Server now provides benchmark data directly - no client-side processing needed
        this.childRowsByParent[rowId] = childRows;
      } catch (error) {
        console.error("Failed to fetch child team data:", error);
        // Remove from expanded state on error
        this.expandedRows.delete(rowId);
      } finally {
        this.expandedRowsLoading[rowId] = false;
      }
    }
  },

  /**
   * Get the depth/indentation level of a team row
   */
  getTeamDepth(row) {
    return row._depth || 0;
  },

  /**
   * Determine if a row itself has children (is expandable parent)
   * Uses row.is_parent when available, otherwise falls back to cached squads
   */
  isRowParent(row) {
    if (row?.is_parent !== undefined) return !!row.is_parent;

    const squads = this.getCachedSquads?.() || [];
    const rowId = this.getRowId(row);
    return squads.some(
      (s) => s?.[this.cachedParentKey] === rowId || s?.parent_id === rowId,
    );
  },

  /**
   * Helper method to safely access nested object properties
   */
  getNestedProperty(obj, path) {
    if (!path || typeof path !== "string") {
      return null;
    }
    return path.split(".").reduce((current, key) => current?.[key], obj);
  },

  /**
   * Consolidated breakdown items getter that handles the common sort → reverse → hydrate pattern
   * This eliminates duplicate code across all breakdown components
   */
  getBreakdownItems(options = {}) {
    // Support both old and new API for backward compatibility
    if (
      typeof options === "string" ||
      options === null ||
      options === undefined
    ) {
      // Old API: getBreakdownItems(dataPath, directData, sortConfig)
      const [
        dataPath = "team_breakdown.data.result_items",
        directData = null,
        sortConfig = null,
      ] = arguments;
      return this._getBreakdownItemsLegacy(dataPath, directData, sortConfig);
    }

    // New API: getBreakdownItems({ dataPath?, directData?, sortBy?, compareTo?, ... })
    const {
      dataPath = "team_breakdown.data.result_items",
      directData = null,
      ...sortConfig
    } = options;

    // Allow direct data to be passed (for allocation component)
    let data = directData;

    if (!data) {
      // Get the data from the specified path
      data = this.getNestedProperty(this.drilldownData, dataPath);
    }

    if (!data || !Array.isArray(data)) {
      return [];
    }

    // Use explicit configuration or fallback to manual getSortFunction or buildSortFunction
    const sortFunction =
      this.getSortFunction?.() ||
      this.buildSortFunction(sortConfig) ||
      this.getDefaultSortFunction();

    // Sort the data with parent/leaf priority
    const sortedItems = [...data].sort((a, b) => {
      // Always sort parents first, regardless of other criteria
      if (a.is_parent !== b.is_parent) {
        return b.is_parent - a.is_parent; // true (1) before false (0)
      }

      // Within parent groups or leaf groups, use the requested sorting
      return sortFunction(a, b);
    });

    // Return hydrated data with tree structure
    // Pass the chosen comparator so child rows use the same sort rules
    return this.hydrateDataWithNestedRows(sortedItems, (a, b) => {
      if (a.is_parent !== b.is_parent) {
        return b.is_parent - a.is_parent;
      }
      return sortFunction(a, b);
    });
  },

  /**
   * Legacy support for old API (will be removed after refactoring)
   */
  _getBreakdownItemsLegacy(
    dataPath = "team_breakdown.data.result_items",
    directData = null,
    sortConfig = null,
  ) {
    // Allow direct data to be passed (for allocation component)
    let data = directData;

    if (!data) {
      // Use default path if dataPath is null/undefined
      const actualDataPath = dataPath || "team_breakdown.data.result_items";
      // Get the data from the specified path
      data = this.getNestedProperty(this.drilldownData, actualDataPath);
    }

    if (!data || !Array.isArray(data)) {
      return [];
    }

    // Use explicit configuration or fallback to manual getSortFunction or buildSortFunction
    const sortFunction =
      this.getSortFunction?.() || this.buildSortFunction(sortConfig);

    // Sort the data with parent/leaf priority
    const sortedItems = [...data].sort((a, b) => {
      // Always sort parents first, regardless of other criteria
      if (a.is_parent !== b.is_parent) {
        return b.is_parent - a.is_parent; // true (1) before false (0)
      }

      // Within parent groups or leaf groups, use the requested sorting
      return sortFunction(a, b);
    });

    // Return hydrated data with tree structure
    // Pass the chosen comparator so child rows use the same sort rules
    return this.hydrateDataWithNestedRows(sortedItems, sortFunction);
  },

  /**
   * Transform flat data into hierarchical structure with nested rows
   * Shows expanded children inline with their parents
   *
   * @param {Array} data - Already-sorted top-level rows
   * @param {Function|Object|null} comparatorOrOptions - Either a row comparator function
   *   or an options object like { rowSortComparator }. When provided, child rows will be
   *   sorted with the same comparator used for top-level rows.
   */
  hydrateDataWithNestedRows(data, comparatorOrOptions = null) {
    const result = [];
    const processedIds = new Set();

    // Sort function for consistency - prefer a provided comparator to keep child rows
    // sorted identically to their parents; fall back to component/default behavior
    let rowSortComparator =
      (typeof comparatorOrOptions === "function" && comparatorOrOptions) ||
      (comparatorOrOptions &&
        typeof comparatorOrOptions.rowSortComparator === "function" &&
        comparatorOrOptions.rowSortComparator) ||
      this.getSortFunction?.() ||
      this.buildSortFunction() ||
      this.getDefaultSortFunction();

    // Helper function to add a row with its nested rows
    const addRowWithNested = (row, depth = 0) => {
      const rowId = this.getRowId(row);
      if (processedIds.has(rowId)) return;

      const parentRow = { ...row, _depth: depth };
      result.push(parentRow);
      processedIds.add(rowId);

      if (this.expandedRows.has(rowId)) {
        if (this.expandedRowsLoading[rowId] && !this.childRowsByParent[rowId]) {
          // Show loading placeholder
          result.push({
            snapshot_squad_id: `placeholder-${rowId}`,
            snapshot_squad_name: "Loading...",
            _depth: depth + 1,
            isFetching: true,
            _isPlaceholder: true, // Keep for backward compatibility
          });
        } else if (this.childRowsByParent[rowId]) {
          // Add child rows recursively
          const children = this.childRowsByParent[rowId];
          // Group expandable (parent) teams together before leaf teams, then apply existing sort
          const groupedSort = (a, b) => {
            const aIsParent = this.isRowParent(a) ? 0 : 1; // parents first
            const bIsParent = this.isRowParent(b) ? 0 : 1;
            if (aIsParent !== bIsParent) return aIsParent - bIsParent;
            return rowSortComparator(a, b);
          };

          children
            .sort(groupedSort)
            .forEach((childRow) => addRowWithNested(childRow, depth + 1));
        }
      }
    };

    // Process top-level rows
    data.forEach((row) => {
      const rowId = this.getRowId(row);
      if (!processedIds.has(rowId)) {
        addRowWithNested(row);
      }
    });

    return result;
  },

  /**
   * Reset all tree expansion state (useful when data changes)
   */
  resetTreeState() {
    this.expandedRows = new Set();
    this.expandedRowsLoading = {};
    this.childRowsByParent = {};
  },

  /**
   * Default sort function - can be overridden by implementing component
   */
  getDefaultSortFunction() {
    return (a, b) => {
      // Sort parents first, then by team name
      if (a.is_parent !== b.is_parent) {
        return b.is_parent - a.is_parent; // true (1) before false (0)
      }
      // If both are parents or both are leaves, sort by name
      return a.snapshot_squad_name.localeCompare(b.snapshot_squad_name);
    };
  },

  /**
   * Build sort function from explicit configuration provided by components
   * This replaces auto-detection with explicit configuration
   */
  buildSortFunction(config = {}) {
    const {
      sortBy = this.drilldownSortBy ||
        (this.tableSort ? this.tableSort.col : "snapshot_squad_name"),
      compareTo = this.compareTo,
      reverse = this.drilldownSortReverse ||
        (this.tableSort ? this.tableSort.dir === "desc" : false),
      ...sortOptions
    } = config;

    // Handle tableSort pattern (allocation)
    if (this.tableSort && this.tableSort.col) {
      return (a, b) => {
        const isDesc = this.tableSort.dir === "desc";

        if (this.tableSort.col === "group_name") {
          return isDesc
            ? b.group_name.localeCompare(a.group_name)
            : a.group_name.localeCompare(b.group_name);
        } else if (this.tableSort.col === "sample_size") {
          const aSize = a.sample_size || 0;
          const bSize = b.sample_size || 0;
          return isDesc ? bSize - aSize : aSize - bSize;
        } else {
          // Handle item-specific sorting
          const aVal = a.items?.[this.tableSort.col];
          const bVal = b.items?.[this.tableSort.col];
          if (aVal == null) return 1;
          if (bVal == null) return -1;
          if (aVal == "hidden" && bVal != "hidden") return 1;
          if (bVal == "hidden" && aVal != "hidden") return -1;
          if (aVal == "hidden" && bVal == "hidden") return 0;
          return isDesc ? bVal - aVal : aVal - bVal;
        }
      };
    }

    // Handle drilldownSortBy pattern (most components)
    if (sortBy) {
      // Get the base sort function with provided options
      const baseSortFunc = sortFuncUtil(sortBy, compareTo, {
        ...sortOptions,
      });

      // Defensive check: if sortFuncUtil didn't return a function, use default
      if (typeof baseSortFunc !== "function") {
        return this.getDefaultSortFunction();
      }

      // Handle reversal logic
      if (!fieldsWithReversibleSortFunctions.includes(sortBy) && reverse) {
        // For non-reversible fields, wrap the sort function to reverse the result
        return (a, b) => {
          const result = baseSortFunc(a, b);
          return -result;
        };
      } else if (fieldsWithReversibleSortFunctions.includes(sortBy)) {
        // For reversible fields, pass the reverse flag to sortFuncUtil
        const reversibleFunc = sortFuncUtil(sortBy, compareTo, {
          ...sortOptions,
          reverse,
        });
        // Defensive check for the reversible case too
        return typeof reversibleFunc === "function"
          ? reversibleFunc
          : this.getDefaultSortFunction();
      } else {
        // Normal sorting (no reversal needed)
        return baseSortFunc;
      }
    }

    // Fallback to default team name sorting
    return this.getDefaultSortFunction();
  },

  /**
   * Sort items with parent/leaf priority - parents first, then by specified criteria
   * This method can be used by components to sort their main result items
   */
  sortItemsWithParentPriority(
    items,
    sortFunc,
    sortBy,
    compareTo,
    sortOptions = {},
  ) {
    // Create the regular sort function with all the provided options
    const regularSortFunc = sortFuncUtil(sortBy, compareTo, sortOptions);

    return [...items].sort((a, b) => {
      // Always sort parents first, regardless of other criteria
      if (a.is_parent !== b.is_parent) {
        return b.is_parent - a.is_parent; // true (1) before false (0)
      }

      // Within parent groups or leaf groups, use the requested sorting
      return regularSortFunc(a, b);
    });
  },

  /**
   * Abstract methods that must be implemented by the using component:
   */

  /**
   * Get the API endpoint for fetching child team data
   * @param {Object} _parentRow - The parent team row being expanded
   * @returns {string} - Full endpoint URL
   */
  // eslint-disable-next-line no-unused-vars
  getChildDataEndpoint(_parentRow) {
    throw new Error(
      "getChildDataEndpoint() must be implemented by the using component",
    );
  },

  /**
   * Get the query parameters for fetching child team data
   * @param {Object} _parentRow - The parent team row being expanded
   * @returns {Object} - Parameters object for the API call
   */
  // eslint-disable-next-line no-unused-vars
  getChildDataParams(_parentRow) {
    throw new Error(
      "getChildDataParams() must be implemented by the using component",
    );
  },

  /**
   * Get the cached squads data for parent/child relationship lookups
   * @returns {Array} - Array of squad objects with id, parent_id, is_parent properties
   */
  getCachedSquads() {
    throw new Error(
      "getCachedSquads() must be implemented by the using component",
    );
  },

  /**
   * Get benchmark data for applying to child rows (optional)
   * @returns {Object} - Benchmark data object with p_50, p_75, p_90 properties
   */
  getBenchmarkData() {
    // Optional method - return empty object if not implemented
    return {};
  },
});
